import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Wallet, Utensils,
  Sparkles, LogOut, Building2,
  Grid3x3, Receipt, LineChart, UserCog, ShieldCheck, Package, Boxes, UserPlus, AlertTriangle, Coins,
  Menu,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePendingApprovals } from "@/context/PendingApprovalsContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, tid: "nav-dashboard" },
  { to: "/residents", label: "Residents", icon: Users, tid: "nav-residents" },
  { to: "/beds", label: "Bed Allocation", icon: Grid3x3, tid: "nav-beds" },
  { to: "/fees", label: "Fees & Payments", icon: Wallet, tid: "nav-fees" },
  { to: "/outstanding", label: "Outstanding Dues", icon: AlertTriangle, tid: "nav-outstanding", financeOnly: true },
  { to: "/residence-fees", label: "Residence Fees", icon: Wallet, tid: "nav-residence-fees" },
  { to: "/mess-fees", label: "Mess Fees", icon: Utensils, tid: "nav-mess-fees" },
  { to: "/expenses", label: "Expenses", icon: Receipt, tid: "nav-expenses" },
  { to: "/petty-cash", label: "Petty Cash", icon: Coins, tid: "nav-petty-cash", financeOnly: true },
  { to: "/assets", label: "Assets", icon: Package, tid: "nav-assets" },
  { to: "/inventory", label: "Inventory", icon: Boxes, tid: "nav-inventory" },
  { to: "/guests", label: "Guest", icon: UserPlus, tid: "nav-guests" },
  { to: "/reports", label: "Reports & P&L", icon: LineChart, tid: "nav-reports", financeOnly: true },
  { to: "/ai", label: "AI Assistant", icon: Sparkles, tid: "nav-ai" },
  { to: "/users", label: "Users & Access", icon: UserCog, tid: "nav-users", elevatedOnly: true },
  { to: "/audit", label: "Audit Log", icon: ShieldCheck, tid: "nav-audit", elevatedOnly: true },
];

function NavLinks({ user, expensesPending, onNavigate }) {
  return (
    <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
      {NAV
        .filter((n) => !n.elevatedOnly || ["admin", "director"].includes(user?.role))
        .filter((n) => !n.financeOnly || ["admin", "accountant", "warden", "hostel_manager", "director"].includes(user?.role))
        .map(({ to, label, icon: Icon, tid }) => (
        <NavLink
          key={to}
          to={to}
          data-testid={tid}
          onClick={onNavigate}
          className={({ isActive }) =>
            `sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-md text-sm min-h-[44px] ${
              isActive
                ? "bg-primary text-primary-foreground"
                : "text-foreground hover:bg-secondary hover:text-primary"
            }`
          }
        >
          <Icon className="w-4 h-4 shrink-0" />
          <span className="flex-1 truncate">{label}</span>
          {to === "/expenses" && expensesPending > 0 && (
            <Badge
              variant="destructive"
              className="ml-auto h-5 min-w-[20px] px-1.5 rounded-full text-[10px] font-bold tabular-nums"
              data-testid="nav-expenses-badge"
              aria-label={`${expensesPending} expense(s) pending your action`}
              title={`${expensesPending} expense(s) waiting for your action`}
            >
              {expensesPending > 99 ? "99+" : expensesPending}
            </Badge>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function BrandHeader() {
  return (
    <div className="flex items-center gap-2">
      <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center shrink-0">
        <Building2 className="w-5 h-5 text-primary-foreground" />
      </div>
      <div className="min-w-0">
        <div className="font-display font-bold text-lg leading-none">Param</div>
        <div className="eyebrow text-muted-foreground mt-1">Hostel ERP</div>
      </div>
    </div>
  );
}

function UserFooter({ user, onLogout }) {
  return (
    <div className="p-4 border-t border-border">
      <div className="text-sm">
        <div className="font-medium truncate">{user?.name}</div>
        <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
        <Badge variant="secondary" className="mt-2 uppercase tracking-wider text-[10px]">
          {user?.role}
        </Badge>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="mt-3 w-full"
        onClick={onLogout}
        data-testid="logout-button"
      >
        <LogOut className="w-4 h-4 mr-2" /> Sign out
      </Button>
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { expensesPending } = usePendingApprovals();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    setMobileOpen(false);
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex md:w-64 flex-col border-r border-border bg-white sticky top-0 h-screen"
        data-testid="app-sidebar"
      >
        <div className="px-6 py-6 border-b border-border">
          <BrandHeader />
        </div>
        <NavLinks user={user} expensesPending={expensesPending} />
        <UserFooter user={user} onLogout={handleLogout} />
      </aside>

      {/* Mobile top nav */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-b border-border px-3 py-2 flex items-center justify-between gap-2">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button size="icon" variant="ghost" aria-label="Open menu" data-testid="mobile-menu-btn">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="w-[85vw] max-w-[300px] p-0 flex flex-col"
            data-testid="mobile-menu-drawer"
          >
            <SheetHeader className="px-4 py-4 border-b border-border">
              <SheetTitle className="text-left">
                <BrandHeader />
              </SheetTitle>
            </SheetHeader>
            <NavLinks
              user={user}
              expensesPending={expensesPending}
              onNavigate={() => setMobileOpen(false)}
            />
            <UserFooter user={user} onLogout={handleLogout} />
          </SheetContent>
        </Sheet>

        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="w-4 h-4 text-primary shrink-0" />
          <div className="font-display font-bold truncate text-sm">Param Hostel ERP</div>
        </div>

        <Button
          size="icon"
          variant="ghost"
          onClick={handleLogout}
          aria-label="Sign out"
          data-testid="mobile-logout"
        >
          <LogOut className="w-4 h-4" />
        </Button>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 pt-14 md:pt-0 w-full">
        <div className="p-4 sm:p-6 md:p-8 max-w-[1600px] mx-auto w-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
